/**
 * Sign in (`/login`) — UI_SDD §4.0, rewritten by DEC-140.
 *
 * One field. **There is no password field anywhere in this application**, and no
 * workspace field either: the workspace is carried by the invitation (DEC-142),
 * never typed and never inferred from what the visitor tells us.
 *
 * This replaces the workspace-ID-plus-admin-secret form, which collected one
 * deployment-wide password, accepted any workspace name including ones that did
 * not exist, and identified nobody. The endpoint behind that form survives as an
 * operator back door with no interface.
 */
import { useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { fetchAuthMethods, initiateGoogleSignIn, requestLoginLink } from '../api/auth'
import { Button } from '../components/shared/Button'
import { PageTitle } from '../components/shared/PageTitle'

export default function LoginPage() {
  const location = useLocation()
  const [email, setEmail] = useState('')
  const [sent, setSent] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)

  // Preserved through the round trip so an expired session returns the user to
  // the page they were on.
  const next = new URLSearchParams(location.search).get('next')


  // Whether this deployment offers Google at all (DEC-144). Asked rather than
  // assumed: rendering the button on a server with no Google app configured
  // produces a control that fails the moment it is clicked.
  const [googleAvailable, setGoogleAvailable] = useState(false)
  const [googleLoading, setGoogleLoading] = useState(false)

  useEffect(() => {
    // A failure here means no Google button, which is the correct degradation:
    // the emailed link works regardless and is never gated on this call.
    fetchAuthMethods()
      .then((m) => setGoogleAvailable(m.google))
      .catch(() => setGoogleAvailable(false))
  }, [])

  async function handleGoogle() {
    setGoogleLoading(true)
    setError(null)
    try {
      window.location.href = await initiateGoogleSignIn(next)
    } catch {
      setError('Could not start Google sign in. Try the email link instead.')
      setGoogleLoading(false)
    }
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!email.trim()) return
    setLoading(true)
    setError(null)
    try {
      await requestLoginLink(email.trim())
      // **Nothing here branches on the response**, because there is nothing in it
      // to branch on. The server answers identically for an enabled address and
      // one that has never been invited, so the login page cannot be used to
      // discover who has access.
      setSent(true)
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : ''
      if (message.includes('429')) {
        setError('Too many requests. Try again in a few minutes.')
      } else if (message.toLowerCase().includes('valid email')) {
        setError('Enter a valid email address.')
      } else if (message.toLowerCase().includes('fetch') || message.toLowerCase().includes('network')) {
        setError('Could not reach the server. Try again in a moment.')
      } else {
        setError('Something went wrong. Try again in a moment.')
      }
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-ground flex flex-col">
      <header className="h-12 border-b border-hairline bg-panel flex items-center px-6">
        <span className="text-[15px] font-semibold text-ink tracking-tight">TDIA</span>
      </header>

      <main className="flex-1 flex items-start justify-center pt-24 px-4">
        <div className="w-full max-w-sm">
          {sent ? (
            <div>
              <PageTitle size="compact">Check your email</PageTitle>
              {/*
                The wording is a §0.6 requirement, not copy polish. The endpoint
                deliberately answers the same for an enabled and an unknown
                address; "We sent you a link" would assert something the server
                did not necessarily do. Stating the condition is what makes the
                non-disclosure a declared property rather than a false claim.
              */}
              <p className="text-[14px] text-muted mt-2 leading-relaxed">
                If that address can sign in or create a workspace, a link is on its way.
                The link works once and expires after a limited time.
              </p>
              <button
                type="button"
                onClick={() => {
                  setSent(false)
                  setError(null)
                }}
                className="mt-6 text-[13px] text-muted underline hover:text-ink"
              >
                Use a different address
              </button>
            </div>
          ) : (
            <>
              <div className="mb-8">
                <PageTitle eyebrow="Tech Debt Intelligence" size="compact">Sign in to TDIA</PageTitle>
                <p className="text-[14px] text-muted mt-1">We will email you a link.</p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label
                    htmlFor="email"
                    className="block text-[13px] font-medium text-body mb-1.5"
                  >
                    Work email
                  </label>
                  <input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@company.com"
                    autoComplete="email"
                    autoFocus
                    disabled={loading}
                    className="w-full border border-control rounded-md px-3 py-2 text-[14px] text-ink placeholder-faint focus:outline-none focus-visible:shadow-focus disabled:opacity-50"
                  />
                </div>

                {error && <p className="text-[13px] text-critical">{error}</p>}

                <Button
                  type="submit"
                  disabled={email.trim().length === 0}
                  loading={loading}
                  className="w-full justify-center py-2.5"
                >
                  Email me a sign in link
                </Button>
              </form>

              {googleAvailable && (
                <>
                  <div className="flex items-center gap-3 my-5">
                    <span className="flex-1 h-px bg-hairline" />
                    <span className="text-[12px] text-faint">or</span>
                    <span className="flex-1 h-px bg-hairline" />
                  </div>

                  <button
                    type="button"
                    onClick={() => void handleGoogle()}
                    disabled={googleLoading}
                    className="w-full flex items-center justify-center gap-2.5 border border-control rounded-md py-2.5 text-[14px] font-medium text-ink hover:bg-fill-subtle focus:outline-none focus-visible:shadow-focus disabled:opacity-50 transition-colors"
                  >
                    {/* Google's mark, inline. Their brand guidelines require the
                        four-colour G and it must not be recoloured. */}
                    <svg width="17" height="17" viewBox="0 0 18 18" aria-hidden="true">
                      <path fill="#4285F4" d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.8 2.72v2.26h2.92c1.7-1.57 2.68-3.88 2.68-6.62Z"/>
                      <path fill="#34A853" d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.92-2.26c-.8.54-1.84.86-3.04.86-2.34 0-4.32-1.58-5.03-3.7H.96v2.33A9 9 0 0 0 9 18Z"/>
                      <path fill="#FBBC05" d="M3.97 10.72a5.4 5.4 0 0 1 0-3.44V4.96H.96a9 9 0 0 0 0 8.08l3.01-2.32Z"/>
                      <path fill="#EA4335" d="M9 3.58c1.32 0 2.5.46 3.44 1.35l2.58-2.58C13.46.89 11.43 0 9 0A9 9 0 0 0 .96 4.96l3.01 2.32C4.68 5.16 6.66 3.58 9 3.58Z"/>
                    </svg>
                    {googleLoading ? 'Redirecting...' : 'Continue with Google'}
                  </button>
                </>
              )}

              <p className="text-[12px] text-faint mt-6 leading-relaxed">
                New to TDIA? Enter your work email and we will send you a link to create
                your workspace. Invited by your team? The same link signs you in to theirs.
              </p>
            </>
          )}

          {next && !sent && (
            <p className="text-[12px] text-faint mt-4">
              You will be returned to where you were after signing in.
            </p>
          )}
        </div>
      </main>
    </div>
  )
}
